/**
 * Travelpayouts Affiliate Link Generator
 * Generates redirect URLs for hotels (via Booking.com after Hotellook shutdown
 * in Oct 2025) and flights (via Aviasales) with the configured marker so
 * commissions are properly attributed.
 *
 * iter65 / iter66 — STRICT NO-FALLBACK POLICY, but boot-safe:
 *   • If `REACT_APP_TRAVELPAYOUTS_MARKER` is missing at build time we
 *     LOG LOUDLY but do NOT throw at module load. Throwing would crash
 *     the entire SPA on the production deploy. Instead URL generators
 *     return "" (no URL); the UI hides the CTA. No untracked traffic
 *     ever leaves the app.
 *
 * Per-distributor promo IDs (Hotels.com / Agoda / Trivago) come from
 * env (REACT_APP_TP_PROMO_HOTELS_COM / _AGODA / _TRIVAGO). When a
 * distributor's promo ID is missing, that distributor is OMITTED from
 * the multi-hotel link list entirely.
 */

import { getBookingLangVariant } from './abTest';
import { isSafePartnerUrl } from './partnerUrl';
import { parseISODate } from './packageItinerary';

function _readEnv(name) {
  const v = (process.env[name] || '').trim();
  if (!v) {
    // eslint-disable-next-line no-console
    console.error(
      `[affiliate] ${name} is NOT SET — affiliate URLs that depend on it ` +
      `will be disabled (no tagless traffic will be routed). Set ${name} ` +
      `in /app/frontend/.env at build time to restore commission attribution.`
    );
    return '';
  }
  return v;
}

const MARKER = _readEnv('REACT_APP_TRAVELPAYOUTS_MARKER');
const SUB_ID = (process.env.REACT_APP_TRAVELPAYOUTS_SUB_ID || 'travelai_web').trim();
const AFFILIATE_ENABLED = /^\d+$/.test(MARKER);

// Optional per-distributor promo IDs. `null` means the distributor is
// disabled (omitted from the multi-hotel link list) — never falls back
// to a default promo_id since that could mis-attribute the click.
// Reserved for when the account qualifies for Hotels.com / Agoda / Trivago
// programs (higher monthly-visitor requirement). Kept as tolerant reads so
// the multi-distributor payload can be re-enabled by dropping the env vars
// back in and reintroducing pushes in `generateMultiHotelLinks`.
void process.env.REACT_APP_TP_PROMO_HOTELS_COM;
void process.env.REACT_APP_TP_PROMO_AGODA;
void process.env.REACT_APP_TP_PROMO_TRIVAGO;

/**
 * Booking.com only reliably geocodes search strings written in the Latin
 * alphabet. When the user is in BG mode, the AI returns Cyrillic city names
 * like "Дубай" — Booking.com responds with a blank "Please visit Booking.com"
 * placeholder page (we saw this in production with `ss=Jumeirah Beach, Дубай`).
 *
 * This map translates the most common destinations our Bulgarian visitors
 * book to their canonical English names. Anything not in the map gets
 * Cyrillic stripped via transliteration as a safety net.
 */
const BG_TO_EN_CITIES = {
  'дубай': 'Dubai', 'оаe': 'United Arab Emirates', 'оае': 'United Arab Emirates',
  'рим': 'Rome', 'италия': 'Italy', 'милано': 'Milan', 'венеция': 'Venice', 'флоренция': 'Florence',
  'париж': 'Paris', 'франция': 'France', 'ница': 'Nice', 'марсилия': 'Marseille',
  'мадрид': 'Madrid', 'испания': 'Spain', 'барселона': 'Barcelona', 'валенсия': 'Valencia', 'севиля': 'Seville', 'малага': 'Malaga', 'ибиса': 'Ibiza',
  'лондон': 'London', 'манчестър': 'Manchester', 'единбург': 'Edinburgh',
  'атина': 'Athens', 'санторини': 'Santorini', 'миконос': 'Mykonos', 'крит': 'Crete', 'родос': 'Rhodes', 'солун': 'Thessaloniki', 'гърция': 'Greece',
  'берлин': 'Berlin', 'мюнхен': 'Munich', 'франкфурт': 'Frankfurt', 'хамбург': 'Hamburg', 'германия': 'Germany',
  'амстердам': 'Amsterdam', 'холандия': 'Netherlands',
  'лисабон': 'Lisbon', 'порто': 'Porto', 'португалия': 'Portugal',
  'виена': 'Vienna', 'австрия': 'Austria', 'прага': 'Prague', 'будапеща': 'Budapest',
  'истанбул': 'Istanbul', 'анталия': 'Antalya', 'турция': 'Turkey',
  'абу даби': 'Abu Dhabi', 'катар': 'Qatar', 'доха': 'Doha',
  'ню йорк': 'New York', 'маями': 'Miami', 'сащ': 'United States',
  'токио': 'Tokyo', 'банкок': 'Bangkok', 'бали': 'Bali', 'сингапур': 'Singapore',
  'малдиви': 'Maldives', 'мароко': 'Morocco', 'мароко': 'Morocco', 'маракеш': 'Marrakech',
  'софия': 'Sofia', 'варна': 'Varna', 'бургас': 'Burgas', 'българия': 'Bulgaria',
};

const CYRILLIC_TO_LATIN = {
  'а':'a','б':'b','в':'v','г':'g','д':'d','е':'e','ж':'zh','з':'z','и':'i','й':'y',
  'к':'k','л':'l','м':'m','н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u',
  'ф':'f','х':'h','ц':'ts','ч':'ch','ш':'sh','щ':'sht','ъ':'a','ь':'','ю':'yu','я':'ya',
};

/**
 * Convert any Cyrillic word/phrase to a Booking.com-friendly Latin form.
 * Uses a curated map for popular destinations, falls back to transliteration.
 * Pure ASCII strings pass through unchanged.
 */
function toLatinSearchString(str) {
  if (!str || typeof str !== 'string') return '';
  // Fast-path: already pure ASCII
  if (/^[\x00-\x7F]+$/.test(str)) return str;

  const lower = str.toLowerCase().trim();
  // Try the curated map first (handles "Дубай" -> "Dubai" cleanly)
  for (const [bg, en] of Object.entries(BG_TO_EN_CITIES)) {
    if (lower.includes(bg)) {
      return str.replace(new RegExp(bg, 'gi'), en);
    }
  }

  // Fallback: per-character transliteration of any remaining Cyrillic
  return str
    .split('')
    .map((ch) => {
      const lc = ch.toLowerCase();
      if (CYRILLIC_TO_LATIN[lc] !== undefined) {
        const tl = CYRILLIC_TO_LATIN[lc];
        return ch === lc ? tl : tl.charAt(0).toUpperCase() + tl.slice(1);
      }
      return ch;
    })
    .join('');
}

/**
 * Heuristic: AI-returned hotel names are essentially ALWAYS invented by the
 * LLM (we don't have a real per-property API). Even ones that look plausible
 * like "S Hotel Boutique Thessaloniki" are fabricated — when we pass them as
 * a Booking.com search string, Booking responds with "Unavailable" and a
 * cold-start "did you mean..." page.
 *
 * For now: treat ALL AI hotel names as generic and route the user to the
 * neighborhood + city + dates + star-tier filtered list. The AI's
 * description acts as inspiration; Booking.com's real inventory is what they
 * actually book. If we ever wire a real per-property API (Booking Demand,
 * Travelopro, Hotelbeds) we'll flip this back.
 */
function looksLikeGenericAiName() {
  return true;
}

/**
 * Generate a hotel booking affiliate link.
 *
 * After Hotellook's API shutdown (Oct 2025), we route hotel traffic to
 * Booking.com with TP attribution via `label` + Travelpayouts partner AID.
 * Booking.com only geocodes Latin-script search strings reliably, so we
 * always normalize Cyrillic input to English before building the URL.
 *
 * Always returns a valid URL — never '#'.
 */
export function generateHotelAffiliateLink({
  neighborhood,
  city,
  cityEn,
  checkin,
  checkout,
  guests = 2,
  children = 0,
  childrenAges = [], // Only actual age data from the traveler, never guesses.
  rooms = 1,
  language,
  stars,         // 3 | 4 | 5  → filter Booking.com to that exact tier
  hotelType,     // "budget" | "standard" | "luxury"
} = {}) {
  // iter66 — refuse to emit a Booking.com URL without the configured
  // marker. Returning "" lets the calling UI hide the CTA gracefully.
  if (!AFFILIATE_ENABLED) return '';
  const adults = Math.max(1, Number(guests) || 2);
  // The booking partner must ask for children's ages if not supplied.
  // Guessed ages silently produce incorrect family room/flight quotes.
  const childCount = Math.min(4, Math.max(0, Math.floor(Number(children) || 0)));

  // Use only valid calendar dates and properly ordered stays; a malformed
  // date silently produces empty partner results and loses travelers.
  if ((checkin && !parseISODate(checkin)) || (checkout && !parseISODate(checkout))
      || (checkin && checkout && checkout <= checkin)) return '';

  // Resolve a Latin-script city Booking.com can actually geocode.
  const latinCity = cityEn || toLatinSearchString(city || '');
  const latinNeighborhood = toLatinSearchString(neighborhood || '');

  // Pick the best search query: neighborhood+city > city > neighborhood.
  // We never use AI-invented hotel names as the search string — see
  // looksLikeGenericAiName comment. They cause "Hotel Unavailable" pages.
  //
  // iter95 · B1 fix — if we have NEITHER a city NOR a neighborhood we
  // MUST NOT silently open Booking.com's featured page (it lands users
  // on Rome / Bali / whatever Booking is promoting). Return "" and let
  // the caller show a clear "destination missing" error to the user.
  let searchQuery = '';
  if (latinNeighborhood && latinCity && latinNeighborhood.toLowerCase() !== latinCity.toLowerCase()) {
    searchQuery = `${latinNeighborhood}, ${latinCity}`;
  } else if (latinCity) {
    searchQuery = latinCity;
  } else if (latinNeighborhood) {
    searchQuery = latinNeighborhood;
  } else {
    // Refuse to generate a URL with no location signal.
    return '';
  }

  // Travelpayouts attribution via `label` (their standard for Booking.com).
  // Embed the A/B variant into the label so server-side conversion data
  // (Travelpayouts dashboard) can also be split by variant later.
  const langVariant = getBookingLangVariant(language);
  const label = `tp-${MARKER}-${SUB_ID}-lng${langVariant.replace('-', '')}`;
  const bookingLang = langVariant;

  // KEEP THIS URL MINIMAL. Booking.com's bot detection trips on long URLs
  // with many filters and serves a blank "Please visit Booking.com" page.
  // We've seen this on mobile in production. Strategy:
  //   - Pass ONLY essential params (ss, dates, occupancy, language, label)
  //   - Append star/property-type filters via `nflt` ONLY when we have a
  //     concrete star tier — these stay short
  //   - Drop `aid`, `selected_currency`, `order` from the base URL because
  //     they trigger the bot/redirect flow on mobile Safari
  const params = new URLSearchParams({
    ss: searchQuery,
    group_adults: String(adults),
    no_rooms: String(rooms),
    group_children: String(childCount),
    label,
    lang: bookingLang,
  });
  // Only transmit actual child ages. If missing, let Booking.com collect
  // them explicitly instead of inventing an eight-year-old for each child.
  if (childCount > 0 && Array.isArray(childrenAges) && childrenAges.length === childCount) {
    const ages = childrenAges.map(age => Number(age));
    if (ages.every(age => Number.isInteger(age) && age >= 0 && age <= 17)) {
      ages.forEach(age => params.append('age', String(age)));
    }
  }

  if (checkin) params.set('checkin', checkin);
  if (checkout) params.set('checkout', checkout);

  // Star filter ONLY (don't mix property-type + class — that combination
  // sometimes returns the blank page on mobile). Star filter alone works
  // reliably.
  let starsFilter = null;
  const explicitStars = stars == null || stars === '' ? NaN : Number(stars);
  // Do not coerce null → 0 → 3 stars, which silently excludes properties.
  // Only an explicit, supported classification may set a star filter.
  if (Number.isInteger(explicitStars) && [3, 4, 5].includes(explicitStars)) {
    starsFilter = explicitStars;
  }
  // Budget, standard and luxury are price/style labels, not verified star
  // classifications. Converting them to 3/4/5-star supplier filters
  // silently removes suitable hotels from the live results.
  if (starsFilter) params.set('nflt', `class=${starsFilter}`);

  return `https://www.booking.com/searchresults.html?${params.toString()}`;
}

/**
 * Multi-distributor hotel links — v3 (iter86)
 *
 * Historically routed to Booking.com + Hotels.com + Agoda + Trivago. As of
 * this launch we only surface **Booking.com** (the only partner whose
 * attribution program is fully activated for our account). Once the account
 * qualifies for the other 3 programs — those require higher monthly visitor
 * flow — we'll simply flip `PROMO_*` env vars back on and re-enable the
 * additional pushes in one line each.
 *
 * Keeps the function signature stable so no caller needs to change: still
 * returns `[{id, label, url, accent}]`.
 */
export function generateMultiHotelLinks({
  neighborhood,
  city,
  cityEn,
  checkin,
  checkout,
  guests = 2,
  children = 0,
  childrenAges = [],
  rooms = 1,
  language,
  stars,
  hotelType,
} = {}) {
  const bookingUrl = generateHotelAffiliateLink({
    neighborhood, city, cityEn, checkin, checkout, guests,
    children, childrenAges, rooms, language, stars, hotelType,
  });
  // Never offer a disabled booking link when the marker or destination is
  // missing. The consumer sees an empty state instead of a broken CTA.
  if (!bookingUrl) return [];

  return [
    { id: 'booking', label: 'Booking.com', url: bookingUrl, accent: 'bg-blue-600 hover:bg-blue-700' },
  ];
}


// ============== AIRLINE NAME → IATA CODE ==============
// Mapping for the most common European/Mediterranean carriers our visitors
// fly. Used to filter Aviasales results to ONLY the airline the user clicked
// in the chat, so they see that specific deal at the top instead of 50
// alternatives.
const AIRLINE_NAME_TO_IATA = {
  // Bulgaria / Europe / LCC
  'wizz air': 'W6', 'wizzair': 'W6', 'визз ер': 'W6', 'визз': 'W6',
  'ryanair': 'FR', 'райънеър': 'FR', 'райънер': 'FR',
  'easyjet': 'U2', 'изиджет': 'U2',
  'pegasus': 'PC', 'пегасус': 'PC',
  'bulgaria air': 'FB', 'българия еър': 'FB',
  // Major flag carriers
  'lufthansa': 'LH', 'луфтханза': 'LH',
  'air france': 'AF', 'еър франс': 'AF',
  'klm': 'KL', 'клм': 'KL',
  'british airways': 'BA', 'бритиш еъруейс': 'BA',
  'turkish airlines': 'TK', 'турски авиолинии': 'TK', 'турските авиолинии': 'TK',
  'austrian': 'OS', 'austrian airlines': 'OS',
  'swiss': 'LX', 'swiss international': 'LX',
  'iberia': 'IB',
  'aegean': 'A3', 'aegean airlines': 'A3',
  'tap portugal': 'TP', 'tap air portugal': 'TP',
  'ita airways': 'AZ', 'alitalia': 'AZ',
  // Middle East & long-haul
  'emirates': 'EK', 'emirates airline': 'EK',
  'qatar airways': 'QR', 'qatar': 'QR',
  'etihad': 'EY', 'etihad airways': 'EY',
  'turkish': 'TK',
};

function airlineToIata(airline) {
  if (!airline || typeof airline !== 'string') return null;
  const lower = airline.toLowerCase().trim();
  // Exact 2-3 letter code already (e.g. "W6")
  if (/^[A-Z0-9]{2,3}$/.test(airline.trim())) return airline.trim();
  // Try direct match
  if (AIRLINE_NAME_TO_IATA[lower]) return AIRLINE_NAME_TO_IATA[lower];
  // Substring match (e.g. "Wizz Air Hungary")
  for (const [name, code] of Object.entries(AIRLINE_NAME_TO_IATA)) {
    if (lower.includes(name)) return code;
  }
  return null;
}

/**
 * Generate an Aviasales flight booking link with the configured marker.
 * Always returns a valid affiliate URL — never '#'.
 *
 * Resolution order:
 *   1. If `deepLink` is a real http(s) URL, normalize and ensure marker+sub_id.
 *   2. If origin+destination+departureDate present, build a search URL.
 *   3. If only origin+destination present, build a partial search URL.
 *   4. Final fallback: aviasales home page with marker (so attribution still works).
 */
/**
 * Travelpayouts/AI sometimes returns CITY codes (MIL, MOW, LON, ...)
 * but Aviasales' web search URL requires AIRPORT IATA codes. Map common
 * city codes to their primary international airport so the redirect
 * actually loads a flight list.
 */
const CITY_TO_AIRPORT = {
  MIL: 'MXP', MOW: 'SVO', LON: 'LHR', PAR: 'CDG', NYC: 'JFK',
  TYO: 'HND', OSA: 'KIX', STO: 'ARN', BUE: 'EZE', WAS: 'IAD', BJS: 'PEK',
  SHA: 'PVG', CHI: 'ORD', YTO: 'YYZ', YMQ: 'YUL', SAO: 'GRU', RIO: 'GIG',
  BHZ: 'CNF', SEL: 'ICN', JKT: 'CGK', IEV: 'KBP', BUH: 'OTP',
};

/**
 * Extract a 3-letter IATA code from a string. AI sometimes returns
 * verbose names like "Sofia (SOF)" or "Rome (FCO)" instead of pure
 * codes; this normalizes them so the affiliate URL is always valid.
 * Falls back to the city→airport map for codes like MIL/MOW that won't
 * resolve in Aviasales' web search.
 * Returns null if no IATA-looking token is found.
 */
function extractIata(value) {
  if (!value || typeof value !== 'string') return null;
  // Prefer parenthesized 3-letter token: "Rome (FCO)" -> "FCO"
  const paren = value.match(/\(([A-Z]{3})\)/);
  if (paren) return CITY_TO_AIRPORT[paren[1]] || paren[1];
  // If the whole string is exactly 3 letters, use it
  const trimmed = value.trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(trimmed)) return CITY_TO_AIRPORT[trimmed] || trimmed;
  return null;
}

export function generateFlightAffiliateLink({ origin, destination, departureDate, returnDate, deepLink, airline, stops, passengers } = {}) {
  // iter66 — refuse to emit an Aviasales URL without the configured
  // marker. Returning "" lets the calling UI hide the CTA gracefully.
  if (!AFFILIATE_ENABLED) return '';
  // Provider results carry full ISO timestamps; the URL builder needs YYYY-MM-DD.
  if (departureDate) departureDate = String(departureDate).slice(0, 10);
  if (returnDate) returnDate = String(returnDate).slice(0, 10);
  // Compute airline filter (if AI gave us an airline name we recognise)
  const airlineCode = airlineToIata(airline);
  // Aviasales URL path ends with the passenger count (1-9). Default 1 for
  // solo. Capped at 9 — Aviasales rejects higher values with a blank page.
  const paxRaw = Number(passengers);
  const paxCount = Number.isFinite(paxRaw) && paxRaw >= 1
    ? Math.min(9, Math.max(1, Math.round(paxRaw)))
    : 1;

  if (deepLink && typeof deepLink === 'string' && isSafePartnerUrl(deepLink)) {
    try {
      const url = new URL(deepLink);
      // Flight links must land on the FLIGHT provider. A Booking.com URL or
      // arbitrary TP-domain link cannot be safely rewritten into one.
      const host = url.hostname.toLowerCase();
      if (host !== 'aviasales.com' && !host.endsWith('.aviasales.com')) {
        throw new Error('Unexpected flight partner domain');
      }
      // Rewrite city codes to airport codes in the /search/ path. Travelpayouts
      // sometimes returns city IATA codes (e.g. MIL) — Aviasales' web
      // search may reject a city code. Map known aliases to airports
      // (Rome airport) so the page actually loads a flight list.
      if (/^\/search\//.test(url.pathname)) {
        const newPath = url.pathname.replace(/[A-Z]{3}/g, (m) => CITY_TO_AIRPORT[m] || m);
        if (newPath !== url.pathname) url.pathname = newPath;
      }
      // Do not trust a provider deep link whose route/dates differ from
      // the itinerary the traveler is booking. An old URL may look plausible
      // while silently pointing to the wrong travel day or destination.
      const expectedOrigin = extractIata(origin);
      const expectedDestination = extractIata(destination);
      if (expectedOrigin && expectedDestination && departureDate) {
        if (!parseISODate(String(departureDate))
            || (returnDate && (!parseISODate(String(returnDate)) || String(returnDate) < String(departureDate)))) {
          return '';
        }
        const ddmm = String(departureDate).slice(8, 10) + String(departureDate).slice(5, 7);
        const retddmm = returnDate
          ? String(returnDate).slice(8, 10) + String(returnDate).slice(5, 7) : '';
        const expectedPrefix = `/search/${expectedOrigin}${ddmm}${expectedDestination}${retddmm}`;
        const actual = url.pathname;
        if (!actual.startsWith(expectedPrefix) || !/^[1-9]$/.test(actual.slice(expectedPrefix.length))) {
          throw new Error('Stale or mismatched flight search deep link');
        }
      }
      // Provider deep links may contain another account's marker; overriding
      // both fields is required to avoid knowingly sending attributed clicks
      // to an unrelated publisher.
      url.searchParams.set('marker', MARKER);
      url.searchParams.set('sub_id', SUB_ID);
      // FORCE EUR currency on every Aviasales redirect — without this, the
      // page geo-detects and shows USD/RUB to some users (we saw $173 vs €189
      // in the chat → broken trust). Override even if the deepLink already
      // had a different currency.
      url.searchParams.set('currency', 'eur');
      url.searchParams.set('locale', 'en');
      if (airlineCode && !url.searchParams.get('airlines')) {
        url.searchParams.set('airlines', airlineCode);
      }
      // BUG #1 FIX: deepLinks from Travelpayouts encode passenger count
      // in the LAST CHARACTER of the path (e.g. /search/SOF1004LIS17041 →
      // trailing "1" = 1 passenger). When we know the actual party size,
      // rewrite that trailing digit so a family of 4 lands on a 4-passenger
      // search instead of a solo one. Only touches paths matching the
      // Aviasales /search/{ORI}{DDMM}{DST}{DDMM}{PAX} or /search/{ORI}{DDMM}{DST}{PAX} shape.
      if (Number.isFinite(paxRaw) && paxRaw >= 1 && /^\/search\/[A-Z]{3}\d{4}[A-Z]{3}(\d{4})?\d$/.test(url.pathname)) {
        url.pathname = url.pathname.slice(0, -1) + String(paxCount);
      }
      return url.toString();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('Failed to parse provided deepLink, building fresh URL:', err);
      /* fall through to construction */
    }
  }

  // FORCE currency + locale so the user always sees the SAME price they
  // clicked in our chat — never USD when they expected EUR.
  const baseParams = new URLSearchParams({
    marker: MARKER,
    sub_id: SUB_ID,
    currency: 'eur',
    locale: 'en',
  });
  // Pre-filter Aviasales results to the SPECIFIC airline the user clicked.
  // Dramatically narrows the result list — they see "Wizz Air €189" first,
  // not 50 alternative carriers.
  if (airlineCode) baseParams.set('airlines', airlineCode);
  // Pre-filter to direct flights only when AI flagged the option as Direct
  if (typeof stops === 'string' && stops.toLowerCase().startsWith('direct')) {
    baseParams.set('direct', 'true');
  }

  // Normalize to 3-letter IATA codes (handles "Sofia (SOF)", "FCO", "Rome (FCO)")
  const originIata = extractIata(origin);
  const destinationIata = extractIata(destination);

  // Build search-style URL if we have origin + destination
  if (originIata && destinationIata && originIata !== destinationIata) {
    let path = '';
    if (departureDate) {
      const dep = String(departureDate);
      if (!parseISODate(dep)) return '';
      if (returnDate && (!parseISODate(String(returnDate)) || returnDate < dep)) return '';
      const depShort = `${dep.slice(8, 10)}${dep.slice(5, 7)}`;
      if (returnDate) {
        const ret = String(returnDate);
        const retShort = `${ret.slice(8, 10)}${ret.slice(5, 7)}`;
        // Round-trip path — Aviasales expects ORI{DDMM}DST{DDMM}{PAX}
        path = `${originIata}${depShort}${destinationIata}${retShort}${paxCount}`;
      } else {
        // One-way path — Aviasales expects ORI{DDMM}DST{PAX}. This branch
        // ONLY fires when the caller explicitly didn't pass returnDate;
        // bookFlight callers must pass the round-trip return date so we
        // don't accidentally downgrade a round-trip recommendation to one
        // way (this was Bug #2 in the iter51 audit).
        path = `${originIata}${depShort}${destinationIata}${paxCount}`;
      }
      return `https://www.aviasales.com/search/${path}?${baseParams.toString()}`;
    }
    // No date — generic results page (carry passengers via query string
    // since the path-encoded form requires a date)
    return `https://www.aviasales.com/search?origin_iata=${originIata}&destination_iata=${destinationIata}&passengers=${paxCount}&${baseParams.toString()}`;
  }

  // Never send a traveler who chose a flight to an unrelated generic homepage.
  return '';
}

export const AFFILIATE_MARKER = MARKER;
